import React, { createContext, useContext } from "react";
import Grid from "@mui/material/Grid";
import { ToggleButton } from "@mui/material";
import dayjs from "dayjs";
import _ from "lodash";
import * as S from "./styles";

type PeriodT = "am" | "pm";

export type DateTimeToolbarContextT = {
  value: string;
  onTimeChange: (hrs: number, mins: number) => void;
  hideTime?: boolean;
  label?: string | null;
};

export const DateTimeToolbarContext = createContext<DateTimeToolbarContextT>({
  value: "",
  onTimeChange: () => undefined,
});

const to24Hour = (hour: number, period: PeriodT) => (hour % 12) + (period === "pm" ? 12 : 0);

const clampToMax = (event: React.ChangeEvent<HTMLInputElement>) =>
  Math.min(parseInt(event.target.value, 10), parseInt(event.target.max, 10));

const DateTimeToolbar = () => {
  const { value, onTimeChange, hideTime = false, label = null } = useContext(DateTimeToolbarContext);
  const selectedDateTime = dayjs(value);
  const period: PeriodT = selectedDateTime.hour() >= 12 ? "pm" : "am";

  const handleHourChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const hour = clampToMax(event);
    onTimeChange(to24Hour(_.isNaN(hour) ? 1 : hour, period), selectedDateTime.minute());
  };

  const handleMinuteChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const minute = clampToMax(event);
    onTimeChange(selectedDateTime.hour(), _.isNaN(minute) ? 0 : minute);
  };

  const handlePeriodChange = (_event: React.MouseEvent<HTMLElement>, nextPeriod: PeriodT | null) => {
    if (!nextPeriod) return;
    onTimeChange(to24Hour(selectedDateTime.hour(), nextPeriod), selectedDateTime.minute());
  };

  return (
    <S.ToolbarContainer container>
      {label && <S.StyledToolbarLabel>{label}</S.StyledToolbarLabel>}
      <S.StyledToolbar hasLabel={!!label}>{selectedDateTime.format("ddd, MMM D")}</S.StyledToolbar>
      {!hideTime && (
        <S.ToolbarTimeContainer>
          <Grid item sx={{ marginRight: "12px" }}>
            <S.StyledInputWrapper>
              <S.StyledInputTime
                type="number"
                min="1"
                max="12"
                defaultValue={parseInt(selectedDateTime.format("hh"), 10)}
                onChange={handleHourChange}
              />
              <S.StyledInputLabel>Hour</S.StyledInputLabel>
            </S.StyledInputWrapper>
            <S.StyledInputTimeColon>:</S.StyledInputTimeColon>
            <S.StyledInputWrapper>
              <S.StyledInputTime
                type="number"
                min="0"
                max="59"
                defaultValue={selectedDateTime.format("mm")}
                onChange={handleMinuteChange}
              />
              <S.StyledInputLabel>Minute</S.StyledInputLabel>
            </S.StyledInputWrapper>
          </Grid>
          <Grid item sx={{ width: "52px" }}>
            <S.StyledToggleButtonGroup value={period} onChange={handlePeriodChange} orientation="vertical" exclusive>
              <ToggleButton value="am">AM</ToggleButton>
              <ToggleButton value="pm">PM</ToggleButton>
            </S.StyledToggleButtonGroup>
          </Grid>
        </S.ToolbarTimeContainer>
      )}
    </S.ToolbarContainer>
  );
};

export default DateTimeToolbar;
